import { createContext, useContext, useState, useEffect, type ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import type { MetaAdAccount } from "@shared/schema";

interface MetaAccountContextType {
  selectedAccountId: number | null;
  selectedAccount: (MetaAdAccount & { connection?: any }) | null;
  setSelectedAccountId: (id: number | null) => void;
  allAccounts: (MetaAdAccount & { connection?: any })[];
  isLoading: boolean;
}

const MetaAccountContext = createContext<MetaAccountContextType>({
  selectedAccountId: null,
  selectedAccount: null,
  setSelectedAccountId: () => {},
  allAccounts: [],
  isLoading: true,
});

export function MetaAccountProvider({ children }: { children: ReactNode }) {
  const [selectedAccountId, setSelectedAccountId] = useState<number | null>(() => {
    const stored = localStorage.getItem("meta-selected-account");
    return stored ? parseInt(stored) : null;
  });

  const { data: accounts = [], isLoading } = useQuery<(MetaAdAccount & { connection?: any })[]>({
    queryKey: ["/api/meta/accounts"],
    retry: false,
    refetchInterval: 30000,
  });

  useEffect(() => {
    if (selectedAccountId !== null) {
      localStorage.setItem("meta-selected-account", selectedAccountId.toString());
    } else {
      localStorage.removeItem("meta-selected-account");
    }
  }, [selectedAccountId]);

  const selectedAccount = selectedAccountId
    ? accounts.find(a => a.id === selectedAccountId) || null
    : null;

  return (
    <MetaAccountContext.Provider
      value={{ selectedAccountId, selectedAccount, setSelectedAccountId, allAccounts: accounts, isLoading }}
    >
      {children}
    </MetaAccountContext.Provider>
  );
}

export function useMetaAccount() {
  return useContext(MetaAccountContext);
}
